/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable max-lines-per-function */
import type { ThemeUICSSObject } from 'theme-ui';

import { breakpointIndexes } from '../breakpoints';

// https://theme-ui.com/components/variants
export type ButtonVariant = 'primary' | 'secondary' | 'secondaryEmphasis';

export type ButtonSize = 'small' | 'medium' | 'large';

const PADDINGS: Record<ButtonSize, { x: number; y: number }> = {
	small: { x: 16, y: 12 },
	medium: { x: 24, y: 16 },
	large: { x: 24, y: 16 },
};

const PADDINGS_ICON_ONLY: Record<ButtonSize, number> = {
	small: 12,
	medium: 16,
	large: 24,
};

// using this approach for a11y for wider screen reader support
// https://www.sarasoueidan.com/blog/accessible-icon-buttons/ (technique #1)
const visuallyVisible = {
	clip: 'initial',
	clipPath: 'initial',
	height: 'auto',
	width: 'auto',
	overflow: 'initial',
	position: 'initial',
	whiteSpace: 'inherit',
};
const visuallyHidden = {
	clip: 'rect(0 0 0 0)',
	clipPath: 'inset(50%)',
	height: '1px',
	width: '1px',
	overflow: 'hidden',
	position: 'absolute',
	whiteSpace: 'nowrap',
	// necessary for this to work within a container with overflow
	top: 0,
	left: 0,
};

const commonButtonStyles: ThemeUICSSObject = {
	cursor: 'pointer',
	fontFamily: 'body',
	fontSize: 18,
	fontWeight: 'bold',
	/*
	 * this generates a list of selector that target a specific data-size-x for a specific media query
	 *
	 * for instance:
	 * @media screen and (min-width: 768px) .css-1udvbqg[data-size-1=small] {
	 *     font-size: 14px;
	 *     line-height: 20px;
	 *     min-height: 36px;
	 * }
	 * @media screen and (min-width: 1024px) .css-1udvbqg[data-size-2=medium] {
	 *     font-size: 16px;
	 *     line-height: 24px;
	 *     min-height: 44px;
	 * }
	 */
	'&': breakpointIndexes.map((idx) => ({
		[`&[data-mode-${idx}=label]`]: {
			borderRadius: 'standard',
			'[data-icon]': {
				display: 'none',
				'&[data-icon-position=start]': {
					marginRight: 0,
					marginLeft: 0,
				},
				'&[data-icon-position=end]': {
					marginRight: 0,
					marginLeft: 0,
				},
			},
			'[data-label]': visuallyVisible,
		},
		[`&[data-mode-${idx}=icon]`]: {
			borderRadius: 'standard',
			'[data-icon]': {
				display: 'block',
				'&[data-icon-position=start]': {
					marginRight: 0,
					marginLeft: 0,
				},
				'&[data-icon-position=end]': {
					marginRight: 0,
					marginLeft: 0,
				},
			},
			'[data-label]': visuallyHidden,
		},
		[`&[data-mode-${idx}="icon+label"]`]: {
			borderRadius: 'standard',
			'[data-icon]': {
				display: 'block',
				'&[data-icon-position=start]': {
					marginRight: 8,
					marginLeft: 0,
				},
				'&[data-icon-position=end]': {
					marginRight: 0,
					marginLeft: 8,
				},
			},
			'[data-label]': visuallyVisible,
		},
		[`&[data-size-${idx}=small]`]: {
			fontSize: 16,
			lineHeight: '24px',
			minHeight: '36px',
			[`&:not([data-mode-${idx}=icon])`]: {
				minWidth: 'auto',
				padding: `${PADDINGS.small.y}px ${PADDINGS.small.x}px`,
			},
			[`&[data-mode-${idx}=icon]`]: {
				minWidth: '36px',
				padding: `${PADDINGS_ICON_ONLY.small}px`,
			},
			'[data-icon]': {
				fontSize: 16,
			},
		},
		[`&[data-size-${idx}=medium]`]: {
			fontSize: 18,
			lineHeight: '20px',
			minHeight: '44px',
			[`&:not([data-mode-${idx}=icon])`]: {
				minWidth: 'auto',
				padding: `${PADDINGS.medium.y}px ${PADDINGS.medium.x}px`,
			},
			[`&[data-mode-${idx}=icon]`]: {
				minWidth: '44px',
				padding: `${PADDINGS_ICON_ONLY.medium}px`,
			},
			'[data-icon]': {
				fontSize: 20,
			},
		},
		[`&[data-size-${idx}=large]`]: {
			fontSize: 18,
			lineHeight: '20px',
			minHeight: '44px',
			[`&:not([data-mode-${idx}=icon])`]: {
				minWidth: 'auto',
				padding: `${PADDINGS.large.y}px ${PADDINGS.large.x}px`,
			},
			[`&[data-mode-${idx}=icon]`]: {
				minWidth: '52px',
				padding: `${PADDINGS_ICON_ONLY.large}px`,
			},
			'[data-icon]': {
				fontSize: 24,
			},
		},
	})),
	'&[data-loading=true]': {
		position: 'relative',
		'[data-label],[data-icon]': {
			visibility: 'hidden',
		},
	},
	// [disabled] for non-button elements (e.g. button links)
	'&:disabled, &:disabled:hover, &[disabled], &[disabled]:hover': {
		backgroundColor: 'sunrise.semantic.fill.disabled',
		backgroundImage: 'none',
		position: 'relative',
		cursor: 'not-allowed',
		'[data-label]': {
			color: 'sunrise.semantic.typography.disabled',
		},
		'[data-icon]': {
			color: 'sunrise.semantic.typography.disabled',
		},
	},
};

export const buttons: Record<ButtonVariant, ThemeUICSSObject> = {
	primary: {
		...commonButtonStyles,
		backgroundColor: 'initial',
		background: 'sunrise.gradient.fill.interactive.default',
		color: 'sunrise.semantic.typography.strong',
		'& > *': {
			color: 'sunrise.semantic.typography.strong',
		},
		'&:hover': {
			background: 'sunrise.gradient.fill.interactive.hover',
		},
		'&:active': {
			background: 'sunrise.gradient.fill.interactive.pressed',
		},
		'&:disabled:before, &:disabled:hover:before, &[disabled]:before, &[disabled]:hover:before': {
			content: '""',
			borderColor: 'transparent',
			borderStyle: 'solid',
			borderRadius: 'standard',
			borderWidth: '1px',
			inset: 0,
			position: 'absolute',
		},
	},
	secondary: {
		...commonButtonStyles,
		bg: 'primary-white',
		color: 'sunrise.semantic.typography.strong',
		position: 'relative',
		'&:before': {
			content: '""',
			borderColor: 'sunrise.semantic.stroke.weak',
			borderStyle: 'solid',
			borderRadius: 'standard',
			borderWidth: '1px',
			inset: 0,
			position: 'absolute',
		},
		'&:hover:before': {
			bg: 'sunrise.semantic.fill.hover',
		},
		'&:active:before, &:focus:before': {
			bg: 'sunrise.semantic.fill.press',
		},
		'[data-label]': {
			color: 'sunrise.semantic.typography.strong',
		},
		'[data-icon]': {
			color: 'currentColor',
		},
		'&:disabled:before, &:disabled:hover:before, &[disabled]:before, &[disabled]:hover:before': {
			content: '""',
			borderColor: 'sunrise.semantic.stroke.disabled',
			borderStyle: 'solid',
			borderRadius: 'standard',
			borderWidth: '1px',
			inset: 0,
			position: 'absolute',
		},
	},
	secondaryEmphasis: {
		...commonButtonStyles,
		bg: 'primary-white',
		color: 'primary-changeRed',
		position: 'relative',
		'&:before': {
			content: '""',
			borderColor: 'currentColor',
			borderStyle: 'solid',
			borderRadius: 'standard',
			borderWidth: '1px',
			inset: 0,
			position: 'absolute',
		},
		'[data-label]': {
			color: 'currentColor',
		},
		'[data-icon]': {
			color: 'currentColor',
		},
		'&:disabled:before, &:disabled:hover:before, &[disabled]:before, &[disabled]:hover:before': {
			content: '""',
			borderColor: 'sunrise.semantic.stroke.disabled',
			borderStyle: 'solid',
			borderRadius: 'standard',
			borderWidth: '1px',
			inset: 0,
			position: 'absolute',
		},
	},
};
